/**
 * Schedule of the Forma synchronisation. The administration chooses an interval (5 to 60 minutes) and a
 * window in local time: every day 06:00-23:59 (default: work on Sundays and late in the evening before a
 * deadline is picked up the same day), Monday to Saturday 06:00-20:59, or around the clock. The server checks
 * every minute whether a run is due, so a changed setting applies at once without a restart. Only active
 * projects are synchronised on schedule (see services/sync.ts syncAll).
 */

export const SYNC_INTERVALS = [5, 10, 15, 30, 60] as const;
export type SyncInterval = (typeof SYNC_INTERVALS)[number];
export const SYNC_WINDOWS = ['daily', 'workhours', 'always'] as const;
export type SyncWindow = (typeof SYNC_WINDOWS)[number];

export interface SyncSchedule {
  syncIntervalMinutes: number;
  syncWindow: SyncWindow;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Weekday (1 = Monday), hour and minute of a moment in the given time zone. */
export function zonedParts(date: Date, timeZone: string): { weekday: number; hour: number; minute: number } {
  const f = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' });
  const parts = Object.fromEntries(f.formatToParts(date).map((p) => [p.type, p.value]));
  return { weekday: WEEKDAYS.indexOf(parts.weekday) + 1, hour: Number(parts.hour) % 24, minute: Number(parts.minute) };
}

export function inSyncWindow(date: Date, window: SyncWindow, timeZone: string): boolean {
  if (window === 'always') return true;
  const p = zonedParts(date, timeZone);
  if (window === 'workhours') return p.weekday !== 7 && p.hour >= 6 && p.hour <= 20;
  // 'daily' (and any unknown value): every day from 06:00 to 23:59
  return p.hour >= 6;
}

/** Incremental runs per week for a schedule (for the load estimate in the administration). */
export function syncRunsPerWeek(s: SyncSchedule): number {
  const interval = SYNC_INTERVALS.includes(s.syncIntervalMinutes as SyncInterval) ? s.syncIntervalMinutes : 15;
  const perHour = 60 / interval;
  if (s.syncWindow === 'always') return 7 * 24 * perHour;
  if (s.syncWindow === 'workhours') return 6 * 15 * perHour;
  return 7 * 18 * perHour;
}

/** True for the minutes at which an incremental run starts (e.g. 15: at :00, :15, :30, :45). */
export function syncDue(date: Date, s: SyncSchedule, timeZone: string): boolean {
  if (!inSyncWindow(date, s.syncWindow, timeZone)) return false;
  const p = zonedParts(date, timeZone);
  const interval = SYNC_INTERVALS.includes(s.syncIntervalMinutes as SyncInterval) ? s.syncIntervalMinutes : 15;
  return (p.hour * 60 + p.minute) % interval === 0;
}

/** Next start after `date` (searches up to eight days ahead). */
export function nextSyncAt(date: Date, s: SyncSchedule, timeZone: string): Date | null {
  let t = Math.floor(date.getTime() / 60_000) * 60_000 + 60_000;
  for (let i = 0; i < 60 * 24 * 8; i++, t += 60_000) {
    const d = new Date(t);
    if (syncDue(d, s, timeZone)) return d;
  }
  return null;
}

/**
 * Rough load of one incremental run: two Issues requests per synchronised project (changed and deleted
 * issues) when everything fits on one page; only active projects count. The API client sends at most 300
 * requests per minute and waits on 429.
 */
export function issuesRequestsPerMinute(syncedProjects: number, s: SyncSchedule): number {
  return (2 * syncedProjects) / Math.max(1, s.syncIntervalMinutes);
}

/** Minutes one incremental run needs at the client's request rate (default 300 per minute). */
export function syncRunMinutes(syncedProjects: number, requestsPerMinute = 300): number {
  return (2 * syncedProjects) / requestsPerMinute;
}
